const API_BASE_URL = '/api';

function getAdminHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('admin_token');
  const headers: Record<string, string> = { ...additionalHeaders };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}


export interface Voucher {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount' | 'free_shipping' | 'gift';
  benefit_data: {
    discount_value?: number;
    max_discount?: number;
    gift_product_id?: string;
  };
  conditions?: {
    min_purchase?: number;
    eligible_product_ids?: string[];
    user_segment?: string;
    registered_only?: boolean;
    active_days?: number[]; // [0 = Minggu, 1 = Senin, ..., 6 = Sabtu]
    schedule_type?: 'fixed' | 'recurring';
    recurring_pattern?: 'double_date' | 'payday' | 'custom';
    recurring_start_day?: number;
    recurring_end_day?: number;
    monthly_reset?: boolean;
  };
  status: 'active' | 'inactive' | 'scheduled';
  usage_limit?: number | null;
  user_usage_limit?: number | null;
  used_count?: number;
  start_date?: string | null;
  end_date?: string | null;
  description?: string;
  created_at?: string;
}

export interface VoucherPopupConfig {
  is_active: boolean;
  image_url: string;
  title?: string;
  subtitle?: string;
  voucher_id?: string;
  voucher_code?: string;
  cta_text?: string;
  target_page: 'all' | 'home' | 'shop';
  delay_seconds: number;
  frequency_hours: number;
}

export interface Order {
  id: string;
  total_amount: number;
  shipping_cost: number;
  discount_amount: number;
  shipping_name: string;
  shipping_phone: string;
  shipping_address: string;
  shipping_city: string;
  shipping_postal_code?: string;
  notes?: string;
  destination_latitude?: number | string | null;
  destination_longitude?: number | string | null;
  shipping_courier?: string;
  shipping_service?: string;
  status: 'pending' | 'processing' | 'shipped' | 'completed' | 'cancelled';
  created_at: string;
}

export async function getAdminVouchers(): Promise<Voucher[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch admin vouchers');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdminVouchers):', error);
    return [];
  }
}

export async function toggleVoucherStatus(id: string, status: 'active' | 'inactive' | 'scheduled'): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/${id}/status`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ status }),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (toggleVoucherStatus):', error);
    return false;
  }
}

export async function getVoucherPopupConfig(): Promise<VoucherPopupConfig | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/popup-config`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('API Error (getVoucherPopupConfig):', error);
    return null;
  }
}

export async function updateVoucherPopupConfig(config: VoucherPopupConfig): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/popup-config`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(config),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (updateVoucherPopupConfig):', error);
    return false;
  }
}

// --- Popup Campaigns (Multi-Campaign System) ---

export type PopupCampaignType = 'promo' | 'banner' | 'launch';
export type PopupCampaignStatus = 'draft' | 'active' | 'scheduled' | 'inactive';

export interface PopupCampaign {
  id: string;
  name: string;
  type: PopupCampaignType;
  status: PopupCampaignStatus;
  priority: number;
  image_url?: string;
  image_aspect_ratio?: '3:2' | '1:1' | '4:5';
  title?: string;
  subtitle?: string;
  cta_text?: string;
  cta_url?: string;
  voucher_id?: string;
  voucher_code?: string;
  launch_date?: string;
  launch_message?: string;
  target_page: 'all' | 'home' | 'shop';
  delay_seconds: number;
  frequency_hours: number;
  start_date?: string;
  end_date?: string;
  active_days?: number[]; // [0 = Minggu, 1 = Senin, ..., 6 = Sabtu]
  schedule_type?: 'fixed' | 'recurring';
  recurring_pattern?: 'double_date' | 'payday' | 'custom' | null;
  recurring_start_day?: number | null;
  recurring_end_day?: number | null;
  recurring_time_start?: string | null;
  recurring_time_end?: string | null;
  created_at?: string;
  updated_at?: string;
}

export async function getPopupCampaigns(): Promise<PopupCampaign[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch popup campaigns');
    return await response.json();
  } catch (error) {
    console.error('API Error (getPopupCampaigns):', error);
    return [];
  }
}

export async function createPopupCampaign(data: Partial<PopupCampaign>): Promise<PopupCampaign | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('API Error (createPopupCampaign):', error);
    return null;
  }
}

export async function updatePopupCampaign(id: string, data: Partial<PopupCampaign>): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (updatePopupCampaign):', error);
    return false;
  }
}

export async function deletePopupCampaign(id: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deletePopupCampaign):', error);
    return false;
  }
}

export async function togglePopupCampaignStatus(id: string, status: PopupCampaignStatus): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups/${id}/status`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ status }),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (togglePopupCampaignStatus):', error);
    return false;
  }
}

export async function getActivePopupForStorefront(): Promise<PopupCampaign | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/popups/active/storefront`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('API Error (getActivePopupForStorefront):', error);
    return null;
  }
}

export async function getVouchers(): Promise<Voucher[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/active`);
    if (!response.ok) throw new Error('Failed to fetch active vouchers');
    return await response.json();
  } catch (error) {
    console.error('API Error (getVouchers):', error);
    return [];
  }
}

export async function createVoucher(data: Partial<Voucher>): Promise<any> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create voucher');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (createVoucher):', error);
    throw error;
  }
}

export async function updateVoucher(id: string, data: Partial<Voucher>): Promise<any> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update voucher');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateVoucher):', error);
    throw error;
  }
}

export async function deleteVoucher(id: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deleteVoucher):', error);
    return false;
  }
}

export async function validateVoucher(code: string, cart_total: number, cart_items: any[]): Promise<any> {
  try {
    const response = await fetch(`${API_BASE_URL}/vouchers/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, cart_total, cart_items }),
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.message || 'Voucher tidak valid');
      (err as any).requiresAuth = data.requires_auth;
      throw err;
    }
    return data;
  } catch (error) {
    console.error('API Error (validateVoucher):', error);
    throw error;
  }
}

export async function getOrders(): Promise<Order[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch orders');
    return await response.json();
  } catch (error) {
    console.error('API Error (getOrders):', error);
    return [];
  }
}

export async function getOrderById(orderId: string): Promise<any> {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch order detail');
    return await response.json();
  } catch (error) {
    console.error('API Error (getOrderById):', error);
    return null;
  }
}

export async function updateOrderStatus(orderId: string, status: string, trackingNumber?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: getAdminHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ status, tracking_number: trackingNumber }),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (updateOrderStatus):', error);
    return false;
  }
}

export async function updateOrderDriverInfo(orderId: string, data: {
  driver_name?: string | null;
  driver_phone?: string | null;
  driver_plate_number?: string | null;
  driver_tracking_url?: string | null;
}): Promise<{ success: boolean; message?: string; order?: any }> {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/driver`, {
      method: 'PATCH',
      headers: getAdminHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    });
    const resData = await response.json();
    return { success: response.ok, message: resData.message || resData.error, order: resData.order };
  } catch (error: any) {
    console.error('API Error (updateOrderDriverInfo):', error);
    return { success: false, message: error.message || 'Gagal memperbarui informasi driver' };
  }
}

export async function getProducts(status?: string, minimal?: boolean, limit?: number) {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (minimal) params.append('minimal', 'true');
    if (limit !== undefined) params.append('limit', String(limit));

    const queryString = params.toString();
    const url = queryString ? `${API_BASE_URL}/products?${queryString}` : `${API_BASE_URL}/products`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch products');
    return await response.json();
  } catch (error) {
    console.error('API Error (getProducts):', error);
    return [];
  }
}

export async function getCategories() {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`);
    if (!response.ok) throw new Error('Failed to fetch categories');
    return await response.json();
  } catch (error) {
    console.error('API Error (getCategories):', error);
    return [];
  }
}

export async function createCategory(name: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Failed to create category');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (createCategory):', error);
    throw error;
  }
}

export async function updateCategory(id: string, data: string | any) {
  try {
    const payload = typeof data === 'string' ? { name: data } : data;
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to update category');
    return await response.json();
  } catch (error) {
    console.error('API Error (updateCategory):', error);
    throw error;
  }
}

export async function deleteCategory(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deleteCategory):', error);
    return false;
  }
}

// === Tags ===

export async function getTags() {
  try {
    const response = await fetch(`${API_BASE_URL}/tags`);
    if (!response.ok) throw new Error('Failed to fetch tags');
    return await response.json();
  } catch (error) {
    console.error('API Error (getTags):', error);
    return [];
  }
}

export async function createTag(name: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/tags`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ name }),
    });
    if (!response.ok) throw new Error('Failed to create tag');
    return await response.json();
  } catch (error) {
    console.error('API Error (createTag):', error);
    throw error;
  }
}

export async function updateTag(id: string, data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/tags/${id}`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update tag');
    return await response.json();
  } catch (error) {
    console.error('API Error (updateTag):', error);
    throw error;
  }
}

export async function deleteTag(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/tags/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deleteTag):', error);
    return false;
  }
}



export async function getProductById(productId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}`);
    if (!response.ok) throw new Error('Failed to fetch product');
    return await response.json();
  } catch (error) {
    console.error('API Error (getProductById):', error);
    return null;
  }
}

export async function getProductBySlug(slug: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/slug/${slug}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch product');
    return await response.json();
  } catch (error) {
    // Only log actual errors, not 404s
    if (error instanceof Error && !error.message.includes('404')) {
      console.error('API Error (getProductBySlug):', error);
    }
    return null;
  }
}

export async function createProduct(productData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(productData),
    });
    if (!response.ok) {
      let errMsg = `Server error (${response.status})`;
      try {
        const errorJson = await response.json();
        if (errorJson?.message) errMsg = errorJson.message;
      } catch (e) {
        const text = await response.text().catch(() => '');
        if (text) errMsg = text;
      }
      throw new Error(errMsg);
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (createProduct):', error);
    throw error;
  }
}

export async function updateProduct(productId: string, productData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(productData),
    });
    if (!response.ok) {
      let errMsg = `Server error (${response.status})`;
      try {
        const errorJson = await response.json();
        if (errorJson?.message) errMsg = errorJson.message;
      } catch (e) {
        const text = await response.text().catch(() => '');
        if (text) errMsg = text;
      }
      throw new Error(errMsg);
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateProduct):', error);
    throw error;
  }
}

export async function deleteProduct(productId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deleteProduct):', error);
    return false;
  }
}

export async function bulkImportProducts(data: {
  products: any[];
  skuStrategy: 'update_existing' | 'skip_existing' | 'force_create';
  defaultStatus: 'active' | 'draft';
}) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/bulk-import`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorText = await response.text();
      let errorJson;
      try {
        errorJson = JSON.parse(errorText);
      } catch (e) {}
      throw new Error(errorJson?.message || `Server error (${response.status}): ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (bulkImportProducts):', error);
    throw error;
  }
}

export async function createOrder(orderData: any, items: any[]) {
  try {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...orderData, items }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error (${response.status}): ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return { success: true };
  } catch (error) {
    console.error('API Error (createOrder):', error);
    throw error;
  }
}


export async function verifyPaymentStatus(orderId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/status/${orderId}`);
    return await response.json();
  } catch (error) {
    console.error('API Error (verifyPaymentStatus):', error);
    return null;
  }
}

export async function initializePayment(orderId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/initialize/${orderId}`);
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Gagal inisialisasi pembayaran');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (initializePayment):', error);
    throw error;
  }
}

export async function chargePayment(orderId: string, paymentMethod: 'qris' | 'bank_transfer', bank?: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/charge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderId,
        payment_method: paymentMethod,
        bank,
      }),
    });
    if (!response.ok) {
      const err = await response.json();
      const detailMessage = Array.isArray(err?.details?.error_messages) ? err.details.error_messages[0] : null;
      throw new Error(detailMessage || err.message || 'Gagal membuat tagihan pembayaran');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (chargePayment):', error);
    throw error;
  }
}

export async function bookShipment(
  orderId: string, 
  deliveryTypeOrCourier: string = 'now',
  deliveryDateOrService?: string,
  deliveryTime?: string,
  courier?: string,
  courierService?: string
) {
  let deliveryType = 'now';
  let deliveryDate: string | undefined = undefined;
  let time: string | undefined = deliveryTime;
  let targetCourier: string | undefined = courier;
  let targetService: string | undefined = courierService;

  const validTypes = ['now', 'later', 'scheduled'];
  if (validTypes.includes(deliveryTypeOrCourier)) {
    deliveryType = deliveryTypeOrCourier;
    deliveryDate = deliveryDateOrService;
  } else {
    // If called as bookShipment(orderId, courier, courierService)
    targetCourier = deliveryTypeOrCourier;
    targetService = deliveryDateOrService;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/logistics/book-shipment`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({ 
        orderId, 
        deliveryType, 
        deliveryDate, 
        deliveryTime: time, 
        courier: targetCourier, 
        courierService: targetService 
      }),
    });
    const result = await response.json();
    if (!response.ok) {
      const customError: any = new Error(result.error || result.message || 'Gagal booking kurir');
      customError.data = result;
      throw customError;
    }
    return result;
  } catch (error) {
    console.error('API Error (bookShipment):', error);
    throw error;
  }
}

export async function cancelShipment(orderId: string, reason: string = 'change_courier') {
  try {
    const response = await fetch(`${API_BASE_URL}/logistics/cancel-shipment`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({ orderId, reason }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || result.message || 'Gagal membatalkan booking kurir');
    }
    return result;
  } catch (error) {
    console.error('API Error (cancelShipment):', error);
    throw error;
  }
}

export async function syncShipmentStatus(orderId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/logistics/sync-shipment-status`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({ orderId }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || result.message || 'Gagal sinkronisasi status pengiriman');
    }
    return result;
  } catch (error) {
    console.error('API Error (syncShipmentStatus):', error);
    throw error;
  }
}

export async function quoteShipmentRate(orderId: string, courier?: string, courierService?: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/logistics/quote-shipment-rate`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({ orderId, courier, courierService }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || result.message || 'Gagal mendapatkan tarif live Biteship');
    }
    return result;
  } catch (error) {
    console.warn('API Warning (quoteShipmentRate):', error);
    throw error;
  }
}

export async function updateOrderLocation(
  orderId: string,
  data: { latitude: number; longitude: number; address?: string; notes?: string }
): Promise<any> {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/location`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Gagal memperbarui lokasi order');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateOrderLocation):', error);
    throw error;
  }
}

export async function getCMSBySlug(slug: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/cms/by-slug/${slug}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`API Error (getCMSBySlug ${slug}):`, error);
    return null;
  }
}

export async function getCMSContent(key: string, options?: { draft?: boolean }) {
  try {
    const url = options?.draft ? `${API_BASE_URL}/cms/${key}?draft=true` : `${API_BASE_URL}/cms/${key}`;
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`API Error (getCMSContent ${key}):`, error);
    return null;
  }
}

export async function saveCMSDraftContent(key: string, content: any): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/cms/${key}/draft`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify(content),
    });
    return response.ok;
  } catch (error) {
    console.error(`API Error (saveCMSDraftContent ${key}):`, error);
    return false;
  }
}

export async function publishCMSContent(key: string, content?: any): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/cms/${key}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: content ? JSON.stringify(content) : JSON.stringify({}),
    });
    return response.ok;
  } catch (error) {
    console.error(`API Error (publishCMSContent ${key}):`, error);
    return false;
  }
}

export async function discardCMSDraftContent(key: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/cms/${key}/discard-draft`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    });
    return response.ok;
  } catch (error) {
    console.error(`API Error (discardCMSDraftContent ${key}):`, error);
    return false;
  }
}

export async function saveCMSContent(key: string, content: any) {
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const parseRetryAfterMs = (value: string | null) => {
    if (!value) return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(0, seconds) * 1000;
    const dateMs = Date.parse(value);
    if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
    return null;
  };

  const globalAny = globalThis as any;
  const inflight: Map<string, Promise<boolean>> =
    globalAny.__seceraInflightCmsSaves ?? (globalAny.__seceraInflightCmsSaves = new Map());
  const lastSaveAt: Map<string, number> =
    globalAny.__seceraLastCmsSaveAt ?? (globalAny.__seceraLastCmsSaveAt = new Map());

  const existing = inflight.get(key);
  if (existing) return existing;

  const now = Date.now();
  const last = lastSaveAt.get(key) ?? 0;
  // Prevent accidental double-submits / hot-refresh storms.
  if (now - last < 1500) {
    console.warn(`saveCMSContent(${key}) skipped: called too frequently.`);
    return false;
  }

  const task = (async () => {
    lastSaveAt.set(key, Date.now());

    const maxAttempts = 4;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(`${API_BASE_URL}/cms/${key}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify(content),
        });

        if (response.ok) return true;
        if (response.status !== 429) return false;

        if (attempt === maxAttempts) return false;

        const retryAfterMs = parseRetryAfterMs(response.headers.get('retry-after'));
        const backoffMs = Math.min(8000, 500 * 2 ** (attempt - 1));
        const jitterMs = Math.floor(Math.random() * 250);
        await sleep((retryAfterMs ?? backoffMs) + jitterMs);
      } catch (error) {
        console.error(`API Error (saveCMSContent ${key}):`, error);
        return false;
      }
    }

    return false;
  })();

  inflight.set(key, task);
  try {
    return await task;
  } finally {
    inflight.delete(key);
  }
}
export type UploadImageSlot = 'hero_16x9' | 'hero_original' | 'product_detail' | 'product_listing' | 'generic';

export async function uploadImage(file: File, slot: UploadImageSlot = 'generic'): Promise<string | null> {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('slot', slot);

  try {
    const response = await fetch(`${API_BASE_URL}/uploads`, {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      return data.url;
    }

    // Surface server-side validation messages to the caller.
    try {
      const err = await response.json();
      throw new Error(err?.message || 'Failed to upload image');
    } catch (parseErr: any) {
      if (parseErr instanceof Error) throw parseErr;
      throw new Error('Failed to upload image');
    }
  } catch (error) {
    console.error('API Error (uploadImage):', error);
    throw error;
  }
}

export async function uploadVideo(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append('video', file);

  try {
    const response = await fetch(`${API_BASE_URL}/uploads/video`, {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
    return null;
  } catch (error) {
    console.error('API Error (uploadVideo):', error);
    return null;
  }
}

export async function uploadDocument(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append('document', file);

  try {
    const response = await fetch(`${API_BASE_URL}/uploads/document`, {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
    return null;
  } catch (error) {
    console.error('API Error (uploadDocument):', error);
    return null;
  }
}

// === Settings ===

export async function getSettings() {
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch settings');
    return await response.json();
  } catch (error) {
    console.error('API Error (getSettings):', error);
    return {};
  }
}

export async function updateSettings(settings: Record<string, any>) {
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(settings),
    });
    if (!response.ok) throw new Error('Failed to update settings');
    return await response.json();
  } catch (error) {
    console.error('API Error (updateSettings):', error);
    throw error;
  }
}

// === Admin User Management ===

export async function getAdminUsers() {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch admin users');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdminUsers):', error);
    return [];
  }
}

export async function createAdminUser(userData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to create admin user');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (createAdminUser):', error);
    throw error;
  }
}

export async function updateAdminUser(id: string, userData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to update admin user');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateAdminUser):', error);
    throw error;
  }
}

export async function deleteAdminUser(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to delete admin user');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (deleteAdminUser):', error);
    throw error;
  }
}

// === Admin Profile Settings ===

export async function updateAdminProfile(data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to update admin profile');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateAdminProfile):', error);
    throw error;
  }
}

export async function changeAdminEmail(data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/change-email`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to change admin email');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (changeAdminEmail):', error);
    throw error;
  }
}

export async function changeAdminPassword(data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to change admin password');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (changeAdminPassword):', error);
    throw error;
  }
}

// === Shipping (Biteship) ===

export async function searchDestination(query: string) {
  const cacheKey = `secera_search_v5_${query.toLowerCase().trim()}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);

    const response = await fetch(`${API_BASE_URL}/shipping/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to search' }));
      throw new Error(err.error || err.message || 'Failed to search');
    }
    const data = await response.json();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  } catch (error) {
    console.error('API Error (searchDestination):', error);
    throw error;
  }
}

// Geocode a Biteship area ID via backend proxy (avoids browser CORS + Nominatim rate limits)
export async function geocodeAreaId(areaId: string): Promise<{ latitude: number; longitude: number } | null> {
  if (!areaId) return null;
  const cacheKey = `secera_geocode_${areaId}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);

    const response = await fetch(`${API_BASE_URL}/shipping/geocode?areaId=${encodeURIComponent(areaId)}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.latitude && data.longitude) {
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
      return data;
    }
    return null;
  } catch (e) {
    console.warn('geocodeAreaId failed (non-critical):', e);
    return null;
  }
}

// Reverse Geocode latitude/longitude to a Biteship Area ID via backend
export async function reverseGeocodeCoords(lat: number, lng: number): Promise<{
  area_id: string;
  label: string;
  city_name: string;
  district_name: string;
  postal_code: string;
  latitude: number;
  longitude: number;
} | null> {
  const cacheKey = `secera_revgeo_${lat.toFixed(3)}_${lng.toFixed(3)}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);

    const response = await fetch(`${API_BASE_URL}/shipping/reverse-geocode?lat=${lat}&lng=${lng}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.area_id) {
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
      return data;
    }
    return null;
  } catch (e) {
    console.warn('reverseGeocodeCoords failed:', e);
    return null;
  }
}

export async function getShippingCost(
  origin: string | number,
  destination: string | number,
  weight: number,
  courier?: string,
  items?: any[],
  destCoords?: { latitude: number; longitude: number }
) {
  const itemsKey = items ? items.map(i => `${i.productId}:${i.quantity}`).join(',') : '';
  const coordsKey = destCoords ? `_${destCoords.latitude.toFixed(4)}_${destCoords.longitude.toFixed(4)}` : '';
  const cacheKey = `secera_cost_${origin}_${destination}_${weight}_${courier || 'all'}_${itemsKey}${coordsKey}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);

    const body: Record<string, any> = { origin, destination, weight, courier, items };
    if (destCoords) {
      body.destination_latitude = destCoords.latitude;
      body.destination_longitude = destCoords.longitude;
    }

    const response = await fetch(`${API_BASE_URL}/shipping/cost`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to calculate shipping' }));
      throw new Error(err.message || 'Failed to calculate shipping');
    }
    const data = await response.json();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  } catch (error) {
    console.error('API Error (getShippingCost):', error);
    throw error;
  }
}

export async function trackOrder(resi: string, courier: string = 'jnt') {
  try {
    const response = await fetch(`${API_BASE_URL}/shipping/track?resi=${encodeURIComponent(resi)}&courier=${encodeURIComponent(courier)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Gagal melacak pesanan');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (trackOrder):', error);
    throw error;
  }
}

export async function trackOrderByOrderId(orderId: string): Promise<{
  success?: boolean;
  history?: any[];
  biteship_status?: string;
  tracking_id?: string;
  summary?: any;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/shipping/track?orderId=${encodeURIComponent(orderId)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Gagal memuat tracking');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (trackOrderByOrderId):', error);
    throw error;
  }
}


// === Customer Auth ===

export async function customerLogin(email: string, password: string) {
  const response = await fetch(`${API_BASE_URL}/customer/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  return { ok: response.ok, data: await response.json() };
}

export async function customerRegister(email: string, password: string, name: string, phone?: string) {
  const response = await fetch(`${API_BASE_URL}/customer/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, name, phone }),
  });
  return { ok: response.ok, data: await response.json() };
}

export async function updateProfile(profileData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/customer/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(profileData),
    });
    if (!response.ok) throw new Error('Failed to update profile');
    return await response.json();
  } catch (error) {
    console.error('API Error (updateProfile):', error);
    throw error;
  }
}

export async function changeCustomerPassword(currentPassword: string, newPassword: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/customer/change-password`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Gagal mengubah password');
    return data;
  } catch (error) {
    console.error('API Error (changeCustomerPassword):', error);
    throw error;
  }
}

// === Order Tracking ===

export async function uploadPaymentProof(orderId: string, proofUrl: string) {
  try {
    const token = localStorage.getItem('customer_token');
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/payment-proof`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ payment_proof_url: proofUrl }),
    });
    if (!response.ok) {
      const err = await response.json();
      return { success: false, message: err.message || 'Gagal mengunggah bukti pembayaran' };
    }
    const data = await response.json();
    return { success: true, message: data.message };
  } catch (error) {
    console.error('API Error (uploadPaymentProof):', error);
    return { success: false, message: 'Koneksi gagal' };
  }
}

export async function getMyOrders() {
  try {
    const token = localStorage.getItem('customer_token');
    const response = await fetch(`${API_BASE_URL}/orders/my`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('API Error (getMyOrders):', error);
    return [];
  }
}

export async function getProductReviews(productId: string, page = 1, pageSize = 6) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/products/${productId}/reviews?status=approved&page=${page}&page_size=${pageSize}`,
      { credentials: 'include' }
    );
    if (!response.ok) return { summary: { avg_rating: 0, count: 0 }, items: [] };
    return await response.json();
  } catch (error) {
    console.error('API Error (getProductReviews):', error);
    return { summary: { avg_rating: 0, count: 0 }, items: [] };
  }
}

export async function getProductReviewSummary(productId: string) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/products/${productId}/reviews?status=approved&page=1&page_size=1`,
      { credentials: 'include' }
    );
    if (!response.ok) return { avg_rating: 0, count: 0 };
    const data = await response.json();
    return {
      avg_rating: Number(data?.summary?.avg_rating || 0),
      count: Number(data?.summary?.count || 0),
    };
  } catch (error) {
    console.error('API Error (getProductReviewSummary):', error);
    return { avg_rating: 0, count: 0 };
  }
}

export async function submitProductReview(input: {
  product_id: string;
  order_id: string;
  rating: number;
  comment: string;
  phone?: string;
  email?: string;
}) {
  const token = localStorage.getItem('customer_token');
  const response = await fetch(`${API_BASE_URL}/reviews`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || 'Gagal mengirim ulasan');
  }
  return response.json();
}
export async function updateCustomerReview(id: number | string, comment: string) {
  const token = localStorage.getItem('customer_token');
  const response = await fetch(`${API_BASE_URL}/reviews/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ comment }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || 'Gagal memperbarui ulasan');
  }
  return response.json();
}

export async function getAdminReviews(status: 'pending' | 'approved' | 'rejected') {
  const response = await fetch(`${API_BASE_URL}/admin/reviews?status=${status}`, {
    headers: getAdminHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch admin reviews');
  return response.json();
}

export async function moderateReview(id: number, action: 'approve' | 'reject', reason?: string) {
  const response = await fetch(`${API_BASE_URL}/admin/reviews/${id}`, {
    method: 'PATCH',
    headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ action, reason }),
  });
  if (!response.ok) throw new Error('Failed to update review');
  return response.json();
}

export async function createAdminReview(input: { product_id: string; rating: number; comment: string; author_name?: string }) {
  const response = await fetch(`${API_BASE_URL}/admin/reviews`, {
    method: 'POST',
    headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || 'Failed to create review');
  }
  return response.json();
}

export async function replyToReview(id: number | string, comment: string, author_name?: string) {
  const response = await fetch(`${API_BASE_URL}/admin/reviews/${id}/reply`, {
    method: 'POST',
    headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ comment, author_name }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || 'Failed to reply to review');
  }
  return response.json();
}

export async function getReviewHistory(id: number | string) {
  const response = await fetch(`${API_BASE_URL}/admin/reviews/${id}/history`, {
    headers: getAdminHeaders(),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch review history');
  return response.json();
}

export async function editAdminReview(id: number | string, comment: string) {
  const response = await fetch(`${API_BASE_URL}/admin/reviews/${id}`, {
    method: 'PATCH',
    headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ action: 'edit', comment }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || 'Failed to edit review');
  }
  return response.json();
}

export async function lookupGuestOrder(orderId: string, phone: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/orders/lookup?order_id=${encodeURIComponent(orderId)}&phone=${encodeURIComponent(phone)}`);
    if (!response.ok) {
      const err = await response.json();
      return { success: false, message: err.message };
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error (lookupGuestOrder):', error);
    return { success: false, message: 'Koneksi gagal' };
  }
}

export async function claimGuestOrder(phone: string) {
  try {
    const token = localStorage.getItem('customer_token');
    const response = await fetch(`${API_BASE_URL}/orders/claim`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ phone }),
    });
    
    const data = await response.json();
    return { success: response.ok, message: data.message, claimed_count: data.claimed_count || 0 };
  } catch (error) {
    console.error('API Error (claimGuestOrder):', error);
    return { success: false, message: 'Koneksi gagal' };
  }
}
// === Customers (CRM) ===

export async function getCustomers() {
  try {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch customers');
    return await response.json();
  } catch (error) {
    console.error('API Error (getCustomers):', error);
    return [];
  }
}

export async function getCustomerByPhone(phone: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/customers/${phone}`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch customer details');
    return await response.json();
  } catch (error) {
    console.error('API Error (getCustomerByPhone):', error);
    return null;
  }
}

// === Notifications ===

export async function getNotifications() {
  try {
    const headers = getAdminHeaders();
    if (!headers['Authorization']) return [];
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      headers
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('API Error (getNotifications):', error);
    return [];
  }
}

export async function getUnreadCount() {
  try {
    const headers = getAdminHeaders();
    if (!headers['Authorization']) return 0;
    const response = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
      headers
    });
    if (response.status === 401) return 0;
    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('API Error (getUnreadCount):', error);
    return 0;
  }
}

export async function markAsRead(id: string) {
  try {
    const headers = getAdminHeaders();
    if (!headers['Authorization']) return false;
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PATCH',
      headers
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (markAsRead):', error);
    return false;
  }
}

export async function markAllAsRead() {
  try {
    const headers = getAdminHeaders();
    if (!headers['Authorization']) return false;
    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'PATCH',
      headers
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (markAllAsRead):', error);
    return false;
  }
}

export async function getCustomerNotifications() {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/customer`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('customer_token')}` },
      credentials: 'include'
    });
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('API Error (getCustomerNotifications):', error);
    return [];
  }
}

export async function getCustomerUnreadCount() {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/customer/unread-count`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('customer_token')}` },
      credentials: 'include'
    });
    if (!response.ok) return 0;
    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('API Error (getCustomerUnreadCount):', error);
    return 0;
  }
}

export async function markCustomerNotificationAsRead(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/customer/${id}/read`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('customer_token')}` },
      credentials: 'include'
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (markCustomerNotificationAsRead):', error);
    return false;
  }
}

export async function getNotificationDeliveries(status?: string) {
  try {
    const url = status ? `${API_BASE_URL}/admin/notification-deliveries?status=${encodeURIComponent(status)}` : `${API_BASE_URL}/admin/notification-deliveries`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
    });
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('API Error (getNotificationDeliveries):', error);
    return [];
  }
}

// === Jobs & Careers ===

export async function getJobs() {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs`);
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return await response.json();
  } catch (error) {
    console.error('API Error (getJobs):', error);
    return [];
  }
}



export async function getJobById(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`);
    if (!response.ok) throw new Error('Failed to fetch job');
    return await response.json();
  } catch (error) {
    console.error('API Error (getJobById):', error);
    return null;
  }
}

export async function getJobBySlug(slug: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/slug/${slug}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch job');
    return await response.json();
  } catch (error) {
    // Only log actual errors, not 404s
    if (error instanceof Error && !error.message.includes('404')) {
      console.error('API Error (getJobBySlug):', error);
    }
    return null;
  }
}

export async function applyForJob(jobId: string, applicationData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(applicationData),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Failed to submit application');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (applyForJob):', error);
    throw error;
  }
}

export async function getAdminJobs() {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/admin/all`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch admin jobs');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdminJobs):', error);
    return [];
  }
}

export async function createJob(jobData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(jobData),
    });
    if (!response.ok) throw new Error('Failed to create job');
    return await response.json();
  } catch (error) {
    console.error('API Error (createJob):', error);
    throw error;
  }
}

export async function updateJob(id: string, jobData: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(jobData),
    });
    if (!response.ok) throw new Error('Failed to update job');
    return await response.json();
  } catch (error) {
    console.error('API Error (updateJob):', error);
    throw error;
  }
}

export async function deleteJob(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders()
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (deleteJob):', error);
    return false;
  }
}

export async function getAdminApplications() {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/admin/applications`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch applications');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdminApplications):', error);
    return [];
  }
}

export async function getApplicationById(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/admin/applications/${id}`, {
      headers: getAdminHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch application details');
    return await response.json();
  } catch (error) {
    console.error('API Error (getApplicationById):', error);
    return null;
  }
}

export async function updateApplicationStatus(id: string, status: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/jobs/admin/applications/${id}`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status }),
    });
    return response.ok;
  } catch (error) {
    console.error('API Error (updateApplicationStatus):', error);
    return false;
  }
}
// === Analytics ===

export async function getAnalyticsSales(start_date: string, end_date: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/sales?start_date=${start_date}&end_date=${end_date}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch sales analytics');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAnalyticsSales):', error);
    throw error;
  }
}

export async function getAnalyticsActivity(start_date: string, end_date: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/activity?start_date=${start_date}&end_date=${end_date}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch activity analytics');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAnalyticsActivity):', error);
    throw error;
  }
}

export async function getAdvancedAnalyticsOverview(params: URLSearchParams) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/advanced/overview?${params.toString()}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch advanced analytics overview');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdvancedAnalyticsOverview):', error);
    throw error;
  }
}

export async function getAdvancedAnalyticsLocation(params: URLSearchParams) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/advanced/location?${params.toString()}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch advanced analytics location');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdvancedAnalyticsLocation):', error);
    throw error;
  }
}

export async function getAdvancedAnalyticsSegments(params: URLSearchParams) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/advanced/segments?${params.toString()}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch advanced analytics segments');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdvancedAnalyticsSegments):', error);
    throw error;
  }
}

export async function getAdvancedAnalyticsTrend(params: URLSearchParams) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/analytics/advanced/trend?${params.toString()}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch advanced analytics trend');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdvancedAnalyticsTrend):', error);
    throw error;
  }
}

export async function getAdvancedAnalyticsLocationDrilldown(locationKey: string, params: URLSearchParams) {
  try {
    const safeLocationKey = encodeURIComponent(locationKey);
    const response = await fetch(`${API_BASE_URL}/admin/analytics/advanced/location/${safeLocationKey}/drilldown?${params.toString()}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch advanced analytics location drilldown');
    return await response.json();
  } catch (error) {
    console.error('API Error (getAdvancedAnalyticsLocationDrilldown):', error);
    throw error;
  }
}

// === BLOG & ARTICLES API CLIENT ENDPOINTS ===

export async function getBlogArticles(params: { category?: string; search?: string; status?: string; limit?: number; offset?: number }) {
  try {
    const query = new URLSearchParams();
    if (params.category) query.append('category', params.category);
    if (params.search) query.append('search', params.search);
    if (params.status) query.append('status', params.status);
    if (params.limit !== undefined) query.append('limit', String(params.limit));
    if (params.offset !== undefined) query.append('offset', String(params.offset));

    let headers: Record<string, string> = {};
    try {
      headers = getAdminHeaders();
    } catch {
      // Non-admin call fallback
    }

    const response = await fetch(`${API_BASE_URL}/blog?${query.toString()}`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch blog articles');
    return await response.json();
  } catch (error) {
    console.error('API Error (getBlogArticles):', error);
    return { articles: [], total: 0, counts: { all: 0, published: 0, draft: 0 } };
  }
}

export async function getBlogCategories() {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/categories`);
    if (!response.ok) throw new Error('Failed to fetch blog categories');
    return await response.json();
  } catch (error) {
    console.error('API Error (getBlogCategories):', error);
    return [];
  }
}

export async function getBlogArticleBySlug(
  slug: string,
  options?: { preview?: boolean; token?: string; password?: string }
) {
  try {
    const query = new URLSearchParams();
    if (options?.preview) query.append('preview', 'true');
    if (options?.token) query.append('token', options.token);
    const qs = query.toString() ? `?${query.toString()}` : '';

    let headers: Record<string, string> = {};
    try {
      headers = getAdminHeaders();
    } catch {
      // Non-admin call fallback
    }

    if (options?.password) {
      headers['x-preview-password'] = options.password;
    }

    const response = await fetch(`${API_BASE_URL}/blog/post/${slug}${qs}`, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const err = new Error(errData.message || 'Failed to fetch article detail');
      (err as any).status = response.status;
      (err as any).code = errData.code;
      (err as any).articleTitle = errData.article_title;
      throw err;
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (getBlogArticleBySlug):', error);
    throw error;
  }
}

export async function createSharePreviewLink(slug: string, expiresIn: string = '24h', password?: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/post/${slug}/share-preview`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ expiresIn, password }),
      credentials: 'include'
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || 'Gagal membuat link pratinjau');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (createSharePreviewLink):', error);
    throw error;
  }
}

export async function publishBlogArticle(id: string, scheduledAt?: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/${id}/publish`, {
      method: 'PATCH',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ scheduled_at: scheduledAt || null }),
      credentials: 'include'
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || 'Gagal mempublikasikan atau menjadwalkan artikel');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (publishBlogArticle):', error);
    throw error;
  }
}

export async function getBlogArticleById(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch article details by ID');
    return await response.json();
  } catch (error) {
    console.error('API Error (getBlogArticleById):', error);
    throw error;
  }
}

export async function saveBlogArticle(id: string | null, data: any) {
  try {
    const url = id ? `${API_BASE_URL}/blog/${id}` : `${API_BASE_URL}/blog`;
    const method = id ? 'PATCH' : 'POST';
    const response = await fetch(url, {
      method,
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    if (!response.ok) {
      const errRes = await response.json().catch(() => ({}));
      throw new Error(errRes.message || 'Failed to save blog article');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (saveBlogArticle):', error);
    throw error;
  }
}

export async function deleteBlogArticle(id: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to delete blog article');
    return await response.json();
  } catch (error) {
    console.error('API Error (deleteBlogArticle):', error);
    throw error;
  }
}

export async function createBlogCategory(name: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/blog/categories`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ name }),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to create blog category');
    return await response.json();
  } catch (error) {
    console.error('API Error (createBlogCategory):', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Email Templates
// ---------------------------------------------------------------------------

export interface EmailTemplate {
  id: string;
  event_type: string;
  recipient_role: 'customer' | 'admin';
  name: string;
  subject: string;
  subtitle: string;
  content: string;
  footer: string;
  action_text: string;
  action_url_pattern: string;
  updated_at?: string;
}

export interface EmailTemplateHistoryEntry {
  id: number;
  changed_by: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/email-templates`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch email templates');
    return await response.json();
  } catch (error) {
    console.error('API Error (getEmailTemplates):', error);
    throw error;
  }
}

export async function getEmailTemplate(id: string): Promise<EmailTemplate> {
  try {
    const response = await fetch(`${API_BASE_URL}/email-templates/${id}`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch email template');
    return await response.json();
  } catch (error) {
    console.error('API Error (getEmailTemplate):', error);
    throw error;
  }
}

export async function updateEmailTemplate(
  id: string,
  data: Partial<Pick<EmailTemplate, 'subject' | 'subtitle' | 'content' | 'footer' | 'action_text' | 'action_url_pattern'>>
): Promise<{ message: string; template: EmailTemplate }> {
  try {
    const response = await fetch(`${API_BASE_URL}/email-templates/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error || 'Failed to update email template');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (updateEmailTemplate):', error);
    throw error;
  }
}

export async function sendTestEmailTemplate(
  id: string,
  targetEmail: string,
  draft?: Partial<Pick<EmailTemplate, 'subject' | 'subtitle' | 'content' | 'footer' | 'action_text' | 'action_url_pattern'>>
): Promise<{ message: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/email-templates/${id}/send-test`, {
      method: 'POST',
      headers: getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ target_email: targetEmail, draft }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error || 'Failed to send test email');
    }
    return await response.json();
  } catch (error) {
    console.error('API Error (sendTestEmailTemplate):', error);
    throw error;
  }
}

export async function getEmailTemplateHistory(id: string): Promise<EmailTemplateHistoryEntry[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/email-templates/${id}/history`, {
      headers: getAdminHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch template history');
    return await response.json();
  } catch (error) {
    console.error('API Error (getEmailTemplateHistory):', error);
    throw error;
  }
}

